/**
 * Origin-bound browser storage for the "Komfort-Login" convenience levels
 * (see IMPLEMENTATION_PLAN.md, section 6.2):
 *
 *  - Stufe A: the raw, still-passphrase-encrypted .p12 file. Only saves the
 *    user a file picker round-trip; the passphrase is never stored.
 *  - Stufe C: a passkey-encrypted copy of the Stufe-A passphrase (see
 *    passkey.ts), attached to the same record -- unlocking it takes a
 *    biometric prompt instead of typing the passphrase.
 *
 * There used to be a Stufe B (caching the imported CryptoKey for the
 * session, no passphrase re-entry, no passkey). Dropped: a user with a
 * working passkey doesn't need it, and everyone else should just type the
 * passphrase -- an in-between "remembered for 30 minutes" tier only added a
 * third case to reason about.
 */
import type { PasskeyProtection } from "./passkey";
import { runTransaction } from "../idb";

const P12_STORE = "p12";
const RECORD_ID = "default";

export interface SavedP12 {
  bytes: ArrayBuffer;
  filename: string;
  savedAt: number;
  passkey?: PasskeyProtection;
}

// --- Stufe A: raw .p12 bytes -------------------------------------------

export async function saveP12ToBrowser(bytes: ArrayBuffer, filename: string): Promise<void> {
  await runTransaction(P12_STORE, "readwrite", (store) =>
    store.put({ bytes, filename, savedAt: Date.now() } satisfies SavedP12, RECORD_ID),
  );
}

export async function loadSavedP12(): Promise<SavedP12 | null> {
  const result = await runTransaction<SavedP12 | undefined>(P12_STORE, "readonly", (store) =>
    store.get(RECORD_ID),
  );
  return result ?? null;
}

export async function removeSavedP12(): Promise<void> {
  await runTransaction(P12_STORE, "readwrite", (store) => store.delete(RECORD_ID));
}

// --- Stufe C: passkey-encrypted passphrase, attached to the Stufe-A record

export async function attachPasskeyToSavedP12(passkey: PasskeyProtection): Promise<void> {
  const existing = await loadSavedP12();
  if (!existing) {
    throw new Error("Kein gespeichertes Zertifikat vorhanden, an das der Passkey angehängt werden könnte.");
  }
  await runTransaction(P12_STORE, "readwrite", (store) =>
    store.put({ ...existing, passkey } satisfies SavedP12, RECORD_ID),
  );
}

export async function removeAll(): Promise<void> {
  await removeSavedP12();
}
